import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useFeatureFlag } from 'featureFlags';
import { useAppSelector } from 'hooks';

import {
  DEBUG_CONSOLE_TOGGLE_EVENT,
  isDebugConsoleEnabled
} from './Debug/debugConsolePreference';

export { isDebugConsoleEnabled, setDebugConsoleEnabled } from './Debug/debugConsolePreference';

/**
 * On-screen console log viewer for native debugging.
 * Intercepts console.log/warn/error and displays them in a
 * scrollable overlay. Tap to toggle expand/collapse.
 * Only renders on native platforms.
 */
const MAX_LINES = 100;

type LogEntry = {
  level: 'log' | 'warn' | 'error';
  msg: string;
  time: string;
};

const DebugConsole = () => {
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  const systemLogAvailable = useFeatureFlag('systemLog');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [expanded, setExpanded] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [preference, setPreference] = useState(() => ({
    identity: playerGUID,
    enabled: isDebugConsoleEnabled(playerGUID)
  }));
  const enabled =
    systemLogAvailable && preference.identity === playerGUID && preference.enabled;

  useEffect(() => {
    // Logs may contain account- or session-specific diagnostics. Never retain
    // them across an authenticated-player boundary on a shared client.
    setLogs([]);
    setExpanded(false);
    const syncPreference = () =>
      setPreference({ identity: playerGUID, enabled: isDebugConsoleEnabled(playerGUID) });
    syncPreference();
    const handler = () => syncPreference();
    window.addEventListener(DEBUG_CONSOLE_TOGGLE_EVENT, handler);
    return () => window.removeEventListener(DEBUG_CONSOLE_TOGGLE_EVENT, handler);
  }, [playerGUID]);

  useEffect(() => {
    if (!enabled) return;

    const origLog = console.log;
    const origWarn = console.warn;
    const origError = console.error;

    const addEntry = (level: LogEntry['level'], args: any[]) => {
      const msg = args.map(a => {
        if (typeof a === 'string') return a;
        try { return JSON.stringify(a)?.substring(0, 200); } catch { return String(a); }
      }).join(' ');

      const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

      setLogs(prev => {
        const next = [...prev, { level, msg, time }];
        return next.length > MAX_LINES ? next.slice(-MAX_LINES) : next;
      });
    };

    const patchedLog = (...args: any[]) => {
      origLog.apply(console, args);
      addEntry('log', args);
    };

    const patchedWarn = (...args: any[]) => {
      origWarn.apply(console, args);
      addEntry('warn', args);
    };

    const patchedError = (...args: any[]) => {
      origError.apply(console, args);
      addEntry('error', args);
    };

    const onWindowError = (e: ErrorEvent) => {
      addEntry('error', [`UNCAUGHT: ${e.message} at ${e.filename}:${e.lineno}`]);
    };

    const onUnhandledRejection = (e: PromiseRejectionEvent) => {
      addEntry('error', [`UNHANDLED PROMISE: ${e.reason?.message || e.reason}`]);
    };

    console.log = patchedLog;
    console.warn = patchedWarn;
    console.error = patchedError;
    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      if (console.log === patchedLog) console.log = origLog;
      if (console.warn === patchedWarn) console.warn = origWarn;
      if (console.error === patchedError) console.error = origError;
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, [enabled]);

  useEffect(() => {
    if (expanded) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, expanded]);

  if (!enabled) return null;

  const levelColor = { log: '#0f0', warn: '#ff0', error: '#f44' };

  return (
    <div
      style={{
        ...containerStyle,
        height: expanded ? '40vh' : '20px',
      }}
      onClick={() => setExpanded(v => !v)}
    >
      {expanded ? (
        <div style={scrollStyle}>
          {logs.map((entry, i) => (
            <div key={i} style={{ color: levelColor[entry.level], marginBottom: '1px' }}>
              <span style={timeStyle}>{entry.time}</span> {entry.msg}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      ) : (
        <div style={collapsedStyle}>
          {logs.length > 0 ? logs[logs.length - 1].msg.substring(0, 60) : 'Debug Console (tap to expand)'}
        </div>
      )}
    </div>
  );
};

export default DebugConsole;

const containerStyle: CSSProperties = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'rgba(0, 0, 0, 0.9)',
  zIndex: 999999,
  fontSize: '0.5rem',
  fontFamily: 'monospace',
  color: '#0f0',
  overflow: 'hidden',
  transition: 'height 0.2s ease',
  cursor: 'pointer',
  borderTop: '1px solid #333',
};

const scrollStyle: CSSProperties = {
  height: '100%',
  overflowY: 'auto',
  padding: '4px 6px',
  wordBreak: 'break-all',
  pointerEvents: 'auto',
};

const collapsedStyle: CSSProperties = {
  padding: '2px 6px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  opacity: 0.7,
};

const timeStyle: CSSProperties = {
  color: '#888',
  marginRight: '4px',
};
